import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { submitContactForm } from '../services/api';
import './Footer.css';

const Footer = ({ language }) => {
  const [formData, setFormData] = useState({
    name: '',
    whatsapp: '',
    telegram: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const translations = {
    ru: {
      zakin: 'Закинь монетку и поддержи проект',
      podder: 'Поддержать',
      zapolni: 'Заполните форму',
      avtor: 'Авторизация',
      regist: 'Регистрация',
      name: 'Имя',
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      whatsapp_placeholder: 'Номер телефона (например: +7 999 123 45 67)',
      telegram_placeholder: 'Telegram username (например: @username)',
      message: 'Сообщение',
      submit: 'Отправить',
      submitting: 'Отправка...',
      success: 'Спасибо! Ваше сообщение успешно отправлено.',
      error: 'Произошла ошибка при отправке сообщения. Попробуйте позже.',
      donate_notice: 'Отправляйте только USDT (TRC20) на этот адрес.',
      menu: [
        { name: 'Объявления', href: '/#workBlock' },
        { name: 'Подать объявление', href: '/#findBlock' }
      ]
    },
    en: {
      zakin: 'Drop a coin and support the project',
      podder: 'To support',
      zapolni: 'Fill out the form',
      avtor: 'Authorization',
      regist: 'Registration',
      name: 'Name',
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      whatsapp_placeholder: 'Phone number (e.g., +1 234 567 8900)',
      telegram_placeholder: 'Telegram username (e.g., @username)',
      message: 'Message',
      submit: 'Submit',
      submitting: 'Submitting...',
      success: 'Thank you! Your message has been sent successfully.',
      error: 'An error occurred while sending the message. Please try again later.',
      donate_notice: 'Send only USDT (TRC20) to this address. Credits after 1 network confirmation.',
      menu: [
        { name: 'Ads', href: '/#workBlock' },
        { name: 'Submit ad', href: '/#findBlock' }
      ]
    },
    it: {
      zakin: 'Lancia una moneta e sostieni il progetto',
      podder: 'Sostenere',
      zapolni: 'Compila il modulo',
      avtor: 'Autorizzazione',
      regist: 'Deposito',
      name: 'Nome',
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      whatsapp_placeholder: 'Numero di telefono (es: +39 123 456 7890)',
      telegram_placeholder: 'Username Telegram (es: @username)',
      message: 'Messaggio',
      submit: 'Invia',
      submitting: 'Invio in corso...',
      success: 'Grazie! Il tuo messaggio è stato inviato con successo.',
      error: 'Si è verificato un errore durante l\'invio del messaggio. Riprova più tardi.',
      menu: [
        { name: 'Annunci', href: '/#workBlock' },
        { name: 'Invia annuncio', href: '/#findBlock' }
      ]
    },
    fr: {
      zakin: 'Jetez une pièce et soutenez le projet',
      podder: 'Soutenir',
      zapolni: 'Remplissez le formulaire',
      avtor: 'Autorisation',
      regist: 'Enregistrement',
      name: 'Nom',
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      whatsapp_placeholder: 'Numéro de téléphone (ex: +33 1 23 45 67 89)',
      telegram_placeholder: 'Nom d\'utilisateur Telegram (ex: @username)',
      message: 'Message',
      submit: 'Envoyer',
      submitting: 'Envoi en cours...',
      success: 'Merci! Votre message a été envoyé avec succès.',
      error: 'Une erreur s\'est produite lors de l\'envoi du message. Veuillez réessayer plus tard.',
      menu: [
        { name: 'Annonces', href: '/#workBlock' },
        { name: 'Soumettre annonce', href: '/#findBlock' }
      ]
    }
  };

  const t = translations[language] || translations.en;
  const location = useLocation();

  useEffect(() => {
    // Handle modal opening/closing based on hash
    const modal = document.getElementById('openModal');

    if (location.hash === '#openModal' && modal) {
      modal.classList.add('show');
      document.body.style.overflow = 'hidden';
    } else {
      if (modal) {
        modal.classList.remove('show');
        document.body.style.overflow = '';
      }
    }
  }, [location.hash]);

  const closeModal = (e) => {
    e.preventDefault();
    const modal = document.getElementById('openModal');
    if (modal) {
      modal.classList.remove('show');
      document.body.style.overflow = '';
    }
    // Remove hash from URL without triggering navigation
    window.history.replaceState(null, null, window.location.pathname + window.location.search);
    // Reset form when closing
    setFormData({ name: '', whatsapp: '', telegram: '', message: '' });
    setSubmitStatus(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear status when user starts typing
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await submitContactForm(formData);

      if (response.data.success) {
        setSubmitStatus('success');
        // Reset form
        setFormData({ name: '', whatsapp: '', telegram: '', message: '' });
        // Close modal after 2 seconds
        setTimeout(() => {
          closeModal(e);
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
      // Show backend error message if available
      if (error.response?.data?.error) {
        setSubmitStatus('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [addressCopied, setAddressCopied] = useState(false);
  const USDT_ADDRESS = "Txxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"; // Placeholder until provided

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(USDT_ADDRESS);
    setAddressCopied(true);
    setTimeout(() => setAddressCopied(false), 2000);
  };

  return (
    <>
      <footer>
        <div className="container">
          <div className="row">
            <div className="footBlock">
              <div className="logo">
                <img src="/local/templates/kapussta/img/logo_white.svg" alt="logo" />
              </div>
              <div className="menu">
                <ul>
                  {t.menu.map((item, index) => (
                    <li key={index}>
                      <a href={item.href}>{item.name}</a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="donate">
                <div className="col-4">
                  <img src="/local/templates/kapussta/img/doll.svg" alt="dollar" />
                </div>
                <div className="col-8">
                  <div className="name">
                    <h4>DONATE</h4>
                  </div>
                  <div className="opis">
                    <p>{t.zakin}</p>
                    <div className="money">
                      <button
                        onClick={() => setIsDonateModalOpen(true)}
                        className="btn btn-support-trigger"
                      >
                        {t.podder}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* USDT Donation Modal */}
      {isDonateModalOpen && (
        <div className="modal-overlay donate-modal-overlay" onClick={() => setIsDonateModalOpen(false)}>
          <div className="modal-content donate-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t.podder} проект</h3>
              <button className="close-modal" onClick={() => setIsDonateModalOpen(false)}>×</button>
            </div>
            <div className="modal-body donate-modal-body">
              <div className="crypto-info">
                <div className="network-badge">Network: TRC20</div>
                <div className="address-container">
                  <span className="address-text">{USDT_ADDRESS}</span>
                  <button className="btn-copy" onClick={handleCopyAddress}>
                    {addressCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="donate-notice">{t.donate_notice}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for "Подать объявление" */}
      <div id="openModal" className="modal">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">{t.zapolni}</h3>
              <a href="#close" title="Close" className="close" onClick={closeModal}>×</a>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="forma">
                  <form onSubmit={handleSubmit}>
                    {submitStatus === 'success' && (
                      <div className="form-message form-success">
                        {t.success}
                      </div>
                    )}
                    {submitStatus === 'error' && (
                      <div className="form-message form-error">
                        {t.error}
                      </div>
                    )}
                    <div className="form-group">
                      <label>{t.name}</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t.whatsapp}</label>
                      <input
                        type="tel"
                        name="whatsapp"
                        value={formData.whatsapp}
                        onChange={handleInputChange}
                        placeholder={t.whatsapp_placeholder}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t.telegram}</label>
                      <input
                        type="text"
                        name="telegram"
                        value={formData.telegram}
                        onChange={handleInputChange}
                        placeholder={t.telegram_placeholder}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', fontStyle: 'italic' }}>
                      * Укажите хотя бы один способ связи (WhatsApp или Telegram)
                    </div>
                    <div className="form-group">
                      <label>{t.message}</label>
                      <textarea
                        name="message"
                        rows="5"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? t.submitting : t.submit}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Footer;


